import AuthService from "@/services/AuthService";
import router from "@/main.router";
import store from "@/store";

// Main function executing almost all the requests
async function send(url, method, data, contentType, headers = {}, optionsParam = {}, isRetrying, displaySpinnerLoader = true) {
  // Check if there is a token in the store from local storage
  store.dispatch("fetchAccessToken");

  // First we wanna get the access token from the store
  var access_token = store.getters.getAccessToken;

  let options = {
    method: method,
    headers: {
      Authorization: `Bearer ${access_token}`,
      From: "web",
      usrId: AuthService.userId,
      ...headers,
    },
    mode: "cors",
    //credentials: 'include'
    ...optionsParam,
  };

  // If we have some data we wanna use it in the body of the request
  if (data && !(data instanceof FormData)) {
    options.body = JSON.stringify(data);
  }
  // if type of fordata we don't wanna json it
  else if (data && data instanceof FormData) {
    options.body = data;
  }

  // If we get a content type we wanna use it
  if (contentType) options.headers["Content-Type"] = contentType;

  // Increment the request counter for the loader
  if (displaySpinnerLoader) {
    store.dispatch("addRemoveRequest", true);
  }

  // Execute the request
  let result = false;

  try {
    result = await fetch(url, options);
  } catch (ex) {
    store.dispatch("setSnackbar", {
      title: "Erreur",
      text: "Le serveur n'est pas accessible.",
      color: "error",
    });
  }

  if (result === false) {
    store.dispatch("addRemoveRequest", false);
    return;
  }

  // If we are unauthorized and not retrying
  if (result.status === 401 && !isRetrying) {
    // The first time we are unauthorized, we wanna try to refresh the token
    var refreshTokenResult = await AuthService.refreshToken();

    // If we got an identity from refresh token flow, we wanna try the request again
    if (refreshTokenResult && AuthService.identity !== null) {
      // Decrement the current number of pending requests before recursive call on itself
      if (displaySpinnerLoader) {
        store.dispatch("addRemoveRequest", false);
      }

      return send(url, method, data, contentType, headers, optionsParam, true);
    }
    // Else
    else {
      // If we are not connected anymore, we wanna redirect to login
      if (!AuthService.isConnected) {
        store.dispatch("setSnackbar", {
          status: "session_timeout",
        });

        // If we are not already redirecting to /login
        // If we are in a multiple API calls case, not testing this would merge the paths and redirect to /loing/toto/login/toto etc.
        if (!router.history.current.fullPath.startsWith("/login")) {
          router.replace({ path: "/login", query: { redirect: router.history.current.fullPath } });
        }
      }
      // If we are still connected we just wanna display the unauthorized message to the user
      else {
        store.dispatch("setSnackbar", {
          status: "401_unauthorized",
        });
      }

      // Decrement the current number of pending requests
      if (displaySpinnerLoader) {
        store.dispatch("addRemoveRequest", false);
      }

      return;
    }
  }

  // Decrement the current number of pending requests
  if (displaySpinnerLoader) {
    store.dispatch("addRemoveRequest", false);
  }

  // Handle HTTP errors
  await checkErrors(result);

  // return result to JSON
  return await toJSON(result);
}

// Handle the HTTP errors and display snackbars
export async function checkErrors(resp) {
  // If the request is OK we just return to the caller
  if (resp.ok) return resp;

  // Get the response as text
  var errorText = await resp.text();

  // Extract HTTP status code
  var statusCode = resp.status;

  // Depending on the status code we wanna display different messages
  var errorTitle = "Oups";
  switch (statusCode) {
    case 403:
      errorTitle = "Interdit";
      break;
    case 404:
      errorTitle = "Non trouvé";
      break;
    case 400:
      errorTitle = "Problème(s)";
      break;
    case 500:
      errorTitle = "Erreur Serveur";
      break;
    default:
      break;
  }

  // If HTTP 400 check for object
  if (statusCode == 400) {
    // Check if the error is an object of array of error messages
    var obj = null;

    // Try catch just to avoid exception on JSON.Parse
    try {
      obj = JSON.parse(errorText);
    } catch (e) {}

    // If we have to handle an object as error messages (list of errors)
    if (obj && obj.errors && Array.isArray(obj.errors)) {
      // For each object of error message
      obj.errors.forEach((error) => {
        store.dispatch("setSnackbar", {
          title: errorTitle,
          text: error,
          status: "error",
          timeout: 2000,
        });
      });
    }
    // else if we have to handle object as properties but in errors property (VM cannot be bound)
    else if (obj && obj.errors && typeof obj.errors === "object") {
      for (const prop in obj.errors) {
        // Check if property is an array
        if (Array.isArray(obj.errors[prop])) {
          // For each object of error message
          obj.errors[prop].forEach((p) => {
            store.dispatch("setSnackbar", {
              title: errorTitle,
              text: `${p}: ${obj.errors[prop][p]}`,
              status: "error",
              timeout: 2000,
            });
          });
        } else {
          store.dispatch("setSnackbar", {
            title: errorTitle,
            text: `${prop}: ${obj[prop]}`,
            status: "error",
            timeout: 2000,
          });
        }
      }
    }
    // else if we have to handle object as properties
    else if (obj) {
      for (const prop in obj) {
        store.dispatch("setSnackbar", {
          title: errorTitle,
          text: `${prop}: ${obj[prop]}`,
          status: "error",
          timeout: 2000,
        });
      }
    }
    // else we have to handle a string as error message
    else {
      // Truncate if too long
      if (errorText.length >= 100) {
        errorText = errorText.substring(0, 100);
        errorText += " ...";
      }

      store.dispatch("setSnackbar", {
        title: errorTitle,
        text: errorText,
        status: "error",
        timeout: 2000,
      });
    }
  }
  // Else if HTTP 403, display a warning
  else if (statusCode == 403) {
    store.dispatch("setSnackbar", {
      title: errorTitle,
      text: "Vousu n'êtes pas autorisé à réaliser cette action.",
      status: "warning",
      timeout: 2000,
    });
  }
  // If not HTTP 400, just display the text
  else {
    // Truncate if too long
    if (errorText.length >= 100) {
      errorText = errorText.substring(0, 100);
      errorText += " ...";
    }

    store.dispatch("setSnackbar", {
      title: errorTitle,
      text: errorText,
      status: "error",
      timeout: 2000,
    });
  }

  // If we arrived here we don't wanna return anything
}

// Export response as JSON
async function toJSON(resp) {
  try {
    const result = await resp.text();
    if (result) return JSON.parse(result);
    else return true;
  } catch (ex) {}
  return false;
}

// UPLOAD a FormData with files
async function upload(url, formData) {
  // Need to ignore the content type in order for fatch to send the FormData
  return send(url, "POST", formData, null, null, { responseType: "Blob" });
}

async function download(url, data) {
  // Check if there is a token in the store from local storage
  store.dispatch("fetchAccessToken");

  // First we wanna get the access token from the store
  var access_token = store.getters.getAccessToken;

  return await fetch(url, {
    method: "GET",
    headers: {
      "Accept-Charset": "utf-8",
      Authorization: `Bearer ${access_token}`,
      From: "web",
      path: data,
    },
    responseType: "Blob",
  }).then(checkErrors);
}

export function postAsync(url, data, displaySpinnerLoader = true) {
  return send(url, "POST", data, "application/json");
}

export function putAsync(url, data) {
  return send(url, "PUT", data, "application/json");
}

export function getAsync(url, headers) {
  return send(url, "GET", null, null, headers);
}

export function deleteAsync(url) {
  return send(url, "DELETE");
}

export function uploadAsync(url, data) {
  return upload(url, data);
}

export function downloadAsync(url, data) {
  return download(url, data);
}
